import {
  Box,
  Grid,
  Typography,
  LinearProgress,
  Checkbox,
} from '@mui/material';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { styled } from '@mui/material/styles';

import React from 'react';
import sortBy from 'sort-by';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ColumnDef } from '@tanstack/react-table';
import { ISuppliersReport, ISuppliersReportPage } from '../../Interface';
import { TanstackTable } from 'Shared/Components/Mui/TanstackTable/index';
import RegisterStructure from 'Shared/Constant/SuppliersReport/RecordStructure';
import { IFieldStructure } from 'Shared/Interfaces/ObjectSalesforce';
import { KEY_SUPPLIERSREPORT_SELECTED } from 'Shared/Constant/Cache';
import ModalSupplierDetail from '../SuppliersReportAssociateModal/index';
import ModalProductDetail from '../SuppliersReportProductModal/index';
import ExportFilePage from './_exportfile';
import convertToNumber from 'Shared/Utils/ConvertToNumber';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

function SuppliersReportFiltersPage(props: ISuppliersReportPage) {

  const [columns, setColumns] = React.useState<ColumnDef<object, any>[]>([]);
  const toggleSetColumns = (values: ColumnDef<object, any>[]) => {
    setColumns(values);
  };

  const [data, setData] = React.useState<object[]>([]);
  const toggleSetData = React.useCallback((values: [ISuppliersReport?], groupMatrix: boolean) => {
    const metaFaturamentoGrouped: Record<string, number> = {};
    const faturamentoGrouped: Record<string, number> = {};
    const listValues = values || [];
    const filteredList = listValues.filter(reg => {


      if (!groupMatrix) {
        return true;
      }
      if (!reg) {
        return false;
      }
      return reg.codigo === reg.codigoPrincipal;
    });

    if (groupMatrix) {
      for (const reg of listValues) {
        if (!reg || !reg.codigoPrincipal) {
          continue;
        }
        const principalCode = reg.codigoPrincipal;
        const metaValue = convertToNumber(reg.metaFaturamento || '0');
        const faturamentoValue = convertToNumber(reg.faturamento || '0');

        faturamentoGrouped[principalCode] = Number(Number((faturamentoGrouped[principalCode] || 0) + faturamentoValue).toFixed(2));
        metaFaturamentoGrouped[principalCode] = Number(((metaFaturamentoGrouped[principalCode] || 0) + metaValue).toFixed(2));
      }
    }

    const _array: object[] = filteredList.map(reg => {
      if (!reg) {
        return {};
      }

      const metaMixValue = groupMatrix ? convertToNumber(reg.metaMixPrincipal || '0') : convertToNumber(reg.metaMix || '0');
      const mixValue = groupMatrix ? convertToNumber(reg.mixFornPrincipal || '0') : convertToNumber(reg.mix || '0');
      const percentMix = metaMixValue > 0 ? (mixValue / metaMixValue) * 100 : 0;

      const groupCode = reg.codigoPrincipal || reg.codigo || '';
      const metaFaturamentoValue = groupMatrix
        ? (metaFaturamentoGrouped[groupCode] ?? 0)
        : convertToNumber(reg.metaFaturamento || '0');
      const faturamentoValue = groupMatrix
        ? (faturamentoGrouped[groupCode] ?? 0)
        : convertToNumber(reg.faturamento || '0');
      const percentFaturamentoValue = metaFaturamentoValue > 0 ? (faturamentoValue / metaFaturamentoValue) * 100 : 0;

      const metaFaturamentoFormatted = metaFaturamentoValue.toLocaleString('pt-BR', {
        minimumIntegerDigits: 1,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      const faturamentoFormatted = faturamentoValue.toLocaleString('pt-BR', {
        minimumIntegerDigits: 1,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return {
        ...reg,
        metaFaturamento: metaFaturamentoFormatted,
        faturamento: faturamentoFormatted,
        percentMix: percentMix.toLocaleString('pt-BR', {
          minimumIntegerDigits: 2,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }),
        percentFaturamento: percentFaturamentoValue.toLocaleString('pt-BR', {
          minimumIntegerDigits: 2,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }),
        percentFaturmento: percentFaturamentoValue.toLocaleString('pt-BR', {
          minimumIntegerDigits: 2,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }),
      };
    });

    setData(_array);
  }, []);
  const [showGroupMatrix, setShowGroupMatrix] = React.useState<boolean>(false);
  React.useEffect(() => {
    setShowGroupMatrix(props.showGroupMatrix ? props.showGroupMatrix : false);
  }, [props.showGroupMatrix])

  const [expanded, setExpanded] = React.useState(true);
  const toggleExpanded = (value: boolean) => {
    setExpanded(value);
  };

  const toggleSetItemSelected = (item: any) => {
    const _itemSuppliersReport: ISuppliersReport = item;

    if (props.toggleSetSuppliersReportSelected) {
      props.toggleSetSuppliersReportSelected(_itemSuppliersReport);
    }
    if (props.toggleSetCache) {
      props.toggleSetCache(KEY_SUPPLIERSREPORT_SELECTED, _itemSuppliersReport);
    }
    if (props.toggleShowModalAssociateDetail) {
      props.toggleShowModalAssociateDetail(true);
    }
  }

  const getFieldsStructure = React.useCallback((list: IFieldStructure[]) => {
    const columnsMeta: ColumnDef<object, any>[] = [];
    if (!list?.length) {
      toggleSetColumns(columnsMeta);
      return;
    }

    list.sort(sortBy('orderFormMain')).forEach((regField) => {
      if (!regField.viewFormMain || !regField.nameField) {
        return;
      }

      const hiddenDefault = regField.hiddenFormMain || false;
      const hiddenCodigo = showGroupMatrix && regField.nameField === 'codigo';
      const hiddenCodigoPrincipal = !showGroupMatrix && regField.nameField === 'codigoPrincipal';
      const hiddenMix = showGroupMatrix && regField.nameField === 'mix';
      const hiddenMixPrincipal = !showGroupMatrix && regField.nameField === 'mixFornPrincipal';
      const hiddenMetaMix = showGroupMatrix && regField.nameField === 'metaMix';
      const hiddenMetaMixPrincipal = !showGroupMatrix && regField.nameField === 'metaMixPrincipal';
      const hiddenField = hiddenDefault || hiddenCodigo || hiddenCodigoPrincipal || hiddenMix || hiddenMixPrincipal || hiddenMetaMix || hiddenMetaMixPrincipal;

      if (hiddenField) {
        return;
      }

      const alignRight = ['currency', 'number'].includes(regField.type || '');

      const columnDef: ColumnDef<object, any> = {
        id: regField.nameField,
        accessorKey: regField.nameField,
        header: regField.label ?? regField.nameField,
        cell: ({ getValue }) => {
          const rawValue = getValue();
          if (regField.type === 'checkbox') {
            return (
              <input
                type="checkbox"
                checked={Boolean(rawValue)}
                readOnly
              />
            );
          }

          if (rawValue === undefined || rawValue === null) {
            return '';
          }

          return rawValue;
        },
        meta: {
          align: alignRight ? 'right' : 'left',
          cellSx: regField.cellStyle,
        },
      };

      columnsMeta.push(columnDef);
    });

    toggleSetColumns(columnsMeta);
  }, [showGroupMatrix]);


  React.useEffect(() => {
    getFieldsStructure(RegisterStructure);
    toggleSetData(props.listSuppliersReport, showGroupMatrix);
  }, [props.listSuppliersReport, props.dateUpdateInfo, getFieldsStructure, toggleSetData, showGroupMatrix])

  const handlersDetails: ISuppliersReportPage = {
    ...props,
    showGroupMatrix,
  };

  return (
    <Box id="SuppliersReportFilter" key="SuppliersReportFilter">
      <Grid container>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Accordion
            expanded={expanded}
            onChange={() => { toggleExpanded(!expanded); }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography component="div" align="left">
                <span className="FormTitle">Resultado</span>
                {props.showLoadingUpdateTable && (
                  <Box component="span" sx={{ display: 'inline-flex', width: 120 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography id="SuppliersReportResultTableExport" component="div" align="right">
                <ExportFilePage {...props} />
              </Typography>
              <Box display="flex" alignItems="center" mb={1} sx={{ width: '100%', backgroundColor: 'transparent' }}>
                <Typography component="div" align="right" sx={{ width: '100%' }}>
                  <Checkbox
                    checked={showGroupMatrix}
                    onChange={(e) => props.toggleShowGroupMatrix ? props.toggleShowGroupMatrix(e.target.checked) : setShowGroupMatrix(e.target.checked)}
                  />
                  <Typography component="span">Agrupar Matriz/Filial</Typography>
                </Typography>
              </Box>
              <Typography id="SuppliersReportResultTable" component="div" align="left">
                <TanstackTable
                  data={data}
                  columns={columns}
                  title="Fornecedores"
                  isLoading={props.showLoadingThisPage}
                  enableGlobalFilter
                  stickyHeader
                  onRowClick={(row) => toggleSetItemSelected(row.original)}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                />
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
      {props.showModalAssociateDetail &&
        <ModalSupplierDetail {...handlersDetails} />
      }
      {props.showModalAssociateProductDetail &&
        <ModalProductDetail {...handlersDetails} />
      }
    </Box>
  );
}

export default SuppliersReportFiltersPage;
